import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Info, Database, RefreshCw, ArrowLeft, TrendingUp, TrendingDown, Minus,
  AlertTriangle, AlertCircle, CheckCircle, ChevronRight, Shield, Activity,
  Clock, FileText, Eye, Zap, BookOpen
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/risk-score-v2`;

const RISK_CLASS_COLORS = { 1: "#16A34A", 2: "#65A30D", 3: "#EAB308", 4: "#EA580C", 5: "#DC2626" };
const RISK_CLASS_LABELS = { 1: "Lowest Risk", 2: "Low Risk", 3: "Moderate Risk", 4: "Elevated Risk", 5: "Highest Risk" };

const fmtCurrency = (v) => {
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const SourceIcon = ({ tooltip }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button className="p-0.5 hover:bg-gray-100 rounded opacity-50 hover:opacity-100"><Database className="h-2.5 w-2.5 text-[#94A3B8]" /></button>
    </TooltipTrigger>
    <TooltipContent><p className="text-[10px] font-mono">{tooltip || "Rippling Data"}</p></TooltipContent>
  </Tooltip>
);

const StatusDot = ({ status }) => {
  const color = status === "good" ? "bg-green-500" : status === "watch" ? "bg-amber-500" : status === "risk" ? "bg-red-500" : "bg-gray-300";
  return <div className={cn("w-2 h-2 rounded-full", color)} />;
};

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAnalystView, setIsAnalystView] = useState(false);
  const [selectedScore, setSelectedScore] = useState(null);
  const [showGlossary, setShowGlossary] = useState(false);
  const [glossary, setGlossary] = useState({});
  const [glossarySearch, setGlossarySearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [detailRes, glossaryRes] = await Promise.all([
          axios.get(`${API}/customers/${customerId}`),
          axios.get(`${API}/glossary`)
        ]);
        setCustomer(detailRes.data);
        setGlossary(glossaryRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [customerId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><RefreshCw className="h-8 w-8 animate-spin text-[#502D3C]" /></div>;
  if (!customer) return <div className="min-h-screen flex items-center justify-center"><p className="text-[#64748B]">Customer not found</p></div>;

  const c = customer;
  const meta = c.taxonomy_metadata || {};

  const scoreCards = [
    { key: "composite_score", value: c.composite_score, delta: c.score_change_30d, color: RISK_CLASS_COLORS[c.risk_class] },
    { key: "bureau_score", value: c.bureau_score, delta: c.taxonomy_deltas?.bureau_score },
    { key: "payment_behavior_score", value: c.payment_behavior_score, delta: c.taxonomy_deltas?.payment_behavior_score },
    { key: "exposure_capacity_score", value: c.exposure_capacity_score, delta: c.taxonomy_deltas?.exposure_capacity_score },
    { key: "business_health_score", value: c.business_health_score, delta: c.taxonomy_deltas?.business_health_score },
    { key: "trend_momentum_score", value: c.trend_momentum_score, delta: c.taxonomy_deltas?.trend_momentum_score },
  ];

  const priorityColor = (p) => p === "critical" ? "bg-red-100 text-red-700 border-red-200" : p === "high" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-gray-100 text-[#64748B] border-gray-200";
  const severityColor = (s) => s === "red" ? "bg-red-50 text-red-600 border-red-200" : "bg-amber-50 text-amber-600 border-amber-200";
  const eventIcon = (type) => {
    switch(type) {
      case "debit_failed": return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
      case "payment_collected": return <CheckCircle className="h-3.5 w-3.5 text-green-500" />;
      case "dpd_crossed": return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
      case "score_changed": return <Activity className="h-3.5 w-3.5 text-blue-500" />;
      case "class_changed": return <Shield className="h-3.5 w-3.5 text-purple-500" />;
      case "watchlist_added": return <Eye className="h-3.5 w-3.5 text-amber-500" />;
      case "bureau_refreshed": return <FileText className="h-3.5 w-3.5 text-[#64748B]" />;
      case "analyst_note": return <FileText className="h-3.5 w-3.5 text-blue-400" />;
      default: return <Clock className="h-3.5 w-3.5 text-[#94A3B8]" />;
    }
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F8FAFC]" data-testid="customer-detail-page">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-[#E2E8F0] px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/risk-score")} className="h-8 text-xs text-[#64748B]" data-testid="back-btn">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />Back to List
              </Button>
              <Separator orientation="vertical" className="h-5" />
              <h1 className="text-lg font-semibold text-[#502D3C]" data-testid="customer-name">{c.customer_name}</h1>
              <Badge variant="outline" className="text-[10px] h-5 bg-gray-50">{c.segment}</Badge>
              <Badge variant="outline" className="text-[10px] h-5">{c.industry}</Badge>
              <Badge variant="outline" className="text-[10px] h-5">{c.region}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowGlossary(true)} className="text-[#64748B] h-8 text-xs">
                <BookOpen className="h-3.5 w-3.5 mr-1" />Glossary
              </Button>
              <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded text-[11px]">
                <span className={!isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Simple</span>
                <Switch checked={isAnalystView} onCheckedChange={setIsAnalystView} className="scale-75" />
                <span className={isAnalystView ? "text-[#502D3C] font-medium" : "text-[#94A3B8]"}>Analyst</span>
              </div>
            </div>
          </div>

          {/* Key metrics row */}
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Badge className="h-6 text-[11px]" style={{ backgroundColor: RISK_CLASS_COLORS[c.risk_class] + '20', color: RISK_CLASS_COLORS[c.risk_class], borderColor: RISK_CLASS_COLORS[c.risk_class] + '40' }}>
                {RISK_CLASS_LABELS[c.risk_class]}
              </Badge>
              <span className="text-xs font-bold text-[#502D3C]">{c.composite_score.toFixed(0)}</span>
              <span className={cn("text-[11px] font-medium flex items-center gap-0.5",
                c.score_change_30d > 0 ? "text-green-600" : c.score_change_30d < 0 ? "text-red-600" : "text-[#94A3B8]")}>
                ({c.score_change_30d > 0 ? "+" : ""}{c.score_change_30d.toFixed(1)})
              </span>
            </div>
            <Separator orientation="vertical" className="h-4" />
            <MetricPill label="Exposure" value={fmtCurrency(c.gross_exposure)} />
            <MetricPill label="Past Due" value={c.past_due > 0 ? fmtCurrency(c.past_due) : "—"} alert={c.past_due > 0} />
            <MetricPill label="Due 7d" value={fmtCurrency(c.due_next_7d)} />
            <MetricPill label="DPD" value={c.dpd > 0 ? `${c.dpd}d` : "—"} alert={c.dpd >= 15} />
            <MetricPill label="Confidence" value={c.score_confidence} />
            {c.watchlist && <Badge variant="outline" className="text-[10px] h-5 bg-amber-50 text-amber-600 border-amber-200">Watchlist</Badge>}
            <Separator orientation="vertical" className="h-4" />
            <span className="text-[10px] text-[#94A3B8]">Owner: {c.owner}</span>
            <span className="text-[10px] text-[#94A3B8]">Reviewed: {c.last_reviewed}</span>
            <span className="text-[10px] text-[#94A3B8]">Refreshed: {c.last_refreshed}</span>
          </div>
        </header>

        <main className="p-4 space-y-4">
          {/* Narrative Banner */}
          <Card className="bg-[#502D3C] text-white border-0" data-testid="narrative-banner">
            <CardContent className="p-4">
              <p className="text-xs text-white/90 leading-relaxed">{c.narrative}</p>
              {c.override_reason && (
                <div className="mt-2 flex items-center gap-1.5 bg-red-500/20 px-2 py-1 rounded">
                  <AlertTriangle className="h-3 w-3 text-red-300" />
                  <p className="text-[11px] text-red-200">Hard Override: {c.override_reason}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SECTION 1: Score Breakdown */}
          <div data-testid="score-breakdown">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#FFCC1C] rounded-full" />
              <span className="text-[10px] font-semibold text-[#502D3C] uppercase tracking-wider">Score Breakdown</span>
            </div>
            <div className="grid grid-cols-6 gap-3">
              {scoreCards.map(({ key, value, delta, color }) => {
                const m = meta[key] || {};
                return (
                  <Card key={key} className={cn("cursor-pointer hover:shadow-sm transition-shadow border-gray-100", key === "composite_score" && "border-l-2")}
                    style={key === "composite_score" ? { borderLeftColor: color } : {}}
                    onClick={() => setSelectedScore(key)} data-testid={`score-${key}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[9px] font-medium text-[#64748B] uppercase tracking-wider leading-tight">{m.label || key.replace(/_/g, ' ')}</p>
                        <div className="flex items-center gap-0.5">
                          <button className="p-0.5 hover:bg-gray-100 rounded opacity-60 hover:opacity-100"><Info className="h-2.5 w-2.5 text-[#94A3B8]" /></button>
                          <SourceIcon tooltip={m.source} />
                        </div>
                      </div>
                      <p className={cn("text-2xl font-bold tracking-tight", value >= 70 ? "text-green-600" : value >= 55 ? "text-[#502D3C]" : value >= 40 ? "text-amber-600" : "text-red-600")}>{value.toFixed(0)}</p>
                      {delta !== undefined && (
                        <span className={cn("text-[10px] font-medium flex items-center gap-0.5 mt-0.5",
                          delta > 2 ? "text-green-600" : delta < -2 ? "text-red-600" : "text-[#94A3B8]")}>
                          {delta > 0.5 ? <TrendingUp className="h-2.5 w-2.5" /> : delta < -0.5 ? <TrendingDown className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />}
                          {delta > 0 ? "+" : ""}{delta.toFixed(1)} (30d)
                        </span>
                      )}
                      {isAnalystView && m.formula && <p className="text-[8px] text-[#94A3B8] mt-1 font-mono truncate">{m.formula}</p>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* SECTION 2: Key Triggers + SECTION 7: Action Center — side by side */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-7" data-testid="triggers-section">
              <Card>
                <CardHeader className="pb-1 pt-3 px-4">
                  <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /><CardTitle className="text-sm font-semibold text-[#502D3C]">Key Triggers</CardTitle></div>
                  <p className="text-[10px] text-[#64748B]">Active signals requiring attention</p>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  {c.triggers.length === 0 ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded"><CheckCircle className="h-4 w-4 text-green-500" /><p className="text-xs text-green-700">No active triggers</p></div>
                  ) : (
                    <div className="space-y-2">
                      {c.triggers.map((t, i) => (
                        <div key={i} className={cn("p-3 rounded border", t.severity === "red" ? "bg-red-50/50 border-red-100" : "bg-amber-50/50 border-amber-100")}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={cn("text-[9px] h-4", severityColor(t.severity))}>{t.label}</Badge>
                              <Badge variant="outline" className="text-[9px] h-4 text-[#64748B]">{t.category}</Badge>
                            </div>
                            <Badge variant="outline" className={cn("text-[9px] h-4", t.severity === "red" ? "text-red-600" : "text-amber-600")}>{t.severity}</Badge>
                          </div>
                          <p className="text-[11px] text-[#64748B] leading-relaxed">{t.reason}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="col-span-5" data-testid="action-center">
              <Card>
                <CardHeader className="pb-1 pt-3 px-4">
                  <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#502D3C]" /><CardTitle className="text-sm font-semibold text-[#502D3C]">Action Center</CardTitle></div>
                  <p className="text-[10px] text-[#64748B]">Recommended actions and controls</p>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-3">
                  {c.actions.length > 0 ? c.actions.map((a, i) => (
                    <div key={i} className="p-2.5 bg-gray-50 rounded border border-gray-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-[#502D3C]">{a.label}</span>
                        <Badge variant="outline" className={cn("text-[9px] h-4", priorityColor(a.priority))}>{a.priority}</Badge>
                      </div>
                      <p className="text-[10px] text-[#64748B]">{a.reason}</p>
                      {a.due_date && <p className="text-[9px] text-[#94A3B8] mt-1">Due: {a.due_date} · Owner: {a.owner}</p>}
                    </div>
                  )) : (
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded"><CheckCircle className="h-4 w-4 text-green-500" /><p className="text-xs text-green-700">No actions required</p></div>
                  )}

                  <Separator />
                  <p className="text-[9px] font-semibold text-[#64748B] uppercase tracking-wider">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {["Add to Watchlist","Assign Review","Collections Outreach","Retry Debit","Verify Funding","Hold Limit Increase","Reduce Limit","Shorten Cadence","Freeze New Spend","Bureau Refresh","Request Financials","Mark as Reviewed"].map(label => (
                      <Button key={label} variant="outline" size="sm" className="h-7 text-[10px] justify-start text-[#64748B] hover:text-[#502D3C] hover:border-[#FFCC1C]" data-testid={`action-${label.toLowerCase().replace(/\s/g,'-')}`}>
                        {label}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* SECTION 3: Exposure & Collections */}
          <div data-testid="exposure-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#502D3C] rounded-full" />
              <span className="text-[10px] font-semibold text-[#502D3C] uppercase tracking-wider">Exposure & Collections</span>
            </div>
            <div className="grid grid-cols-12 gap-3 mb-4">
              <MetricCard title="Gross Exposure" value={fmtCurrency(c.gross_exposure)} source="finance.card_receivable_daily" span={2} />
              <MetricCard title="Current / Not Due" value={fmtCurrency(c.current_not_due)} source="finance.card_receivable_daily" span={2} />
              <MetricCard title="Past Due" value={c.past_due > 0 ? fmtCurrency(c.past_due) : "—"} source="finance.card_receivable_daily" span={2} alert={c.past_due > 0} />
              <MetricCard title="Due Next 7 Days" value={fmtCurrency(c.due_next_7d)} source="finance.card_due_schedule" span={2} />
              <MetricCard title="Due Next 30 Days" value={fmtCurrency(c.due_next_30d)} source="finance.card_due_schedule" span={2} />
              <MetricCard title="DPD" value={c.dpd > 0 ? `${c.dpd} days` : "Current"} source="finance.card_receivable_daily" span={2} alert={c.dpd >= 15} />
            </div>
            <div className="grid grid-cols-12 gap-3 mb-4">
              <MetricCard title="On-Time Collection" value={`${c.on_time_collection}%`} source="finance.card_collection_attempt" span={2} />
              <MetricCard title="1st Attempt Success" value={`${c.first_attempt_success}%`} source="finance.card_collection_attempt" span={2} alert={c.first_attempt_success < 80} />
              <MetricCard title="Failed Debits (30d)" value={c.failed_debits_30d} source="finance.card_collection_attempt" span={2} alert={c.failed_debits_30d > 0} />
              <MetricCard title="Failed Debits (90d)" value={c.failed_debits_90d} source="finance.card_collection_attempt" span={2} />
              <MetricCard title="Expected Credit Loss" value={fmtCurrency(c.ecl)} subValue={`${(c.ecl_rate * 100).toFixed(2)}% rate`} source="risk.card_expected_loss_daily" span={2} />
              <MetricCard title="Net Write-offs" value={c.net_writeoffs > 0 ? fmtCurrency(c.net_writeoffs) : "—"} source="finance.card_writeoff" span={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <MiniChart title="Balance Trend" data={c.balance_trend} dataKey="value" formatter={fmtCurrency} color="#502D3C" source="finance.card_receivable_daily" />
              <MiniChart title="Past Due Trend" data={c.past_due_trend} dataKey="value" formatter={fmtCurrency} color="#DC2626" source="finance.card_receivable_daily" />
              <MiniChart title="Payment Success Trend" data={c.payment_success_trend} dataKey="value" formatter={v => `${v}%`} color="#16A34A" source="finance.card_collection_attempt" />
              <MiniChart title="Score Trend" data={c.score_trend} dataKey="value" formatter={v => v.toFixed(0)} color="#FFCC1C" source="risk.customer_composite_score_daily" />
            </div>
          </div>

          {/* SECTION 4: Bureau View + SECTION 5: Internal Health — side by side */}
          <div className="grid grid-cols-2 gap-4">
            <div data-testid="bureau-section">
              <Card>
                <CardHeader className="pb-1 pt-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#502D3C]">Bureau / External View</CardTitle>
                    <SourceIcon tooltip="external.commercial_bureau" />
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-2">
                  <InfoRow label="Commercial Bureau Score" value={c.commercial_bureau_score} />
                  <InfoRow label="Bureau Trend" value={<TrendBadge trend={c.bureau_trend} />} />
                  <InfoRow label="Trade Payment Score" value={c.trade_payment_score?.toFixed(1)} />
                  <InfoRow label="Recent Delinquencies" value={c.recent_delinquencies} alert={c.recent_delinquencies > 0} />
                  <InfoRow label="Derogatory Flags" value={c.derogatory_flags} alert={c.derogatory_flags > 0} />
                  <InfoRow label="File Depth" value={c.file_depth} />
                  <InfoRow label="Last Bureau Refresh" value={c.last_bureau_refresh} />
                  {c.bureau_trend_data && (
                    <div className="mt-3">
                      <MiniChart title="Bureau Score Trend" data={c.bureau_trend_data} dataKey="value" formatter={v => v} color="#6366F1" source="external.commercial_bureau" compact />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div data-testid="internal-health-section">
              <Card>
                <CardHeader className="pb-1 pt-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-[#502D3C]">Rippling Internal Health</CardTitle>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 bg-[#FFCC1C] rounded flex items-center justify-center"><span className="text-[#502D3C] text-[8px] font-bold">R</span></div>
                      <SourceIcon tooltip="core.company_dim + payroll.*" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-2">
                  <InfoRow label="Payroll Stability" value={<HealthBadge value={c.payroll_stability} />} />
                  <InfoRow label="Headcount Trend" value={<TrendBadge trend={c.headcount_trend?.toLowerCase()} customLabels={{ growing: "Growing", stable: "Stable", declining: "Declining" }} />} />
                  <InfoRow label="Payroll Funding Success" value={`${c.payroll_funding_success}%`} />
                  <InfoRow label="Cash Trend" value={<HealthBadge value={c.cash_trend} />} />
                  <InfoRow label="Relationship Depth" value={c.relationship_depth} />
                  <InfoRow label="Spend Anomaly" value={c.spend_anomaly ? <Badge variant="outline" className="text-[9px] h-4 bg-amber-50 text-amber-600 border-amber-200">Detected</Badge> : "None"} />
                  <InfoRow label="Tenure" value={`${c.tenure_days} days (${Math.floor(c.tenure_days / 30)} cycles)`} />
                  {isAnalystView && (
                    <div className="mt-2 p-2 bg-[#502D3C]/5 rounded border border-[#502D3C]/10">
                      <p className="text-[9px] text-[#502D3C] font-medium flex items-center gap-1">
                        <span className="w-3 h-3 bg-[#FFCC1C] rounded text-[7px] flex items-center justify-center font-bold text-[#502D3C]">R</span>
                        Why Rippling can see this
                      </p>
                      <p className="text-[10px] text-[#64748B] mt-0.5">Rippling processes payroll, headcount, and funding — giving real-time visibility into business health that no external bureau provides.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* SECTION 6: Timeline */}
          <Card data-testid="timeline-section">
            <CardHeader className="pb-1 pt-3 px-4">
              <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#64748B]" /><CardTitle className="text-sm font-semibold text-[#502D3C]">Event Timeline</CardTitle></div>
              <p className="text-[10px] text-[#64748B]">Recent account events and changes</p>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-0">
                {c.timeline.map((event, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                    <div className="flex-shrink-0 mt-0.5">{eventIcon(event.event_type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#502D3C]">{event.description}</p>
                      <p className="text-[10px] text-[#94A3B8] mt-0.5">{event.date}</p>
                    </div>
                    {event.severity && <Badge variant="outline" className={cn("text-[9px] h-4 flex-shrink-0",
                      event.severity === "red" ? "text-red-500 border-red-200" : event.severity === "warning" ? "text-amber-500 border-amber-200" : "text-green-500 border-green-200"
                    )}>{event.severity}</Badge>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </main>

        {/* Score Detail Sheet */}
        <Sheet open={!!selectedScore} onOpenChange={() => setSelectedScore(null)}>
          <SheetContent className="w-[420px]">
            <SheetHeader><SheetTitle className="text-[#502D3C]">{meta[selectedScore]?.label || selectedScore}</SheetTitle></SheetHeader>
            {selectedScore && meta[selectedScore] && (
              <ScrollArea className="h-[calc(100vh-100px)] mt-4">
                <div className="space-y-4 pr-3">
                  <div><h4 className="text-xs font-semibold text-[#502D3C] mb-1">What is this?</h4><p className="text-xs text-[#64748B] leading-relaxed">{meta[selectedScore].definition}</p></div>
                  <div><h4 className="text-xs font-semibold text-[#502D3C] mb-1">Formula</h4><p className="text-xs font-mono bg-gray-50 p-2 rounded border border-gray-100">{meta[selectedScore].formula}</p></div>
                  {meta[selectedScore].positive_drivers?.length > 0 && (
                    <div><h4 className="text-xs font-semibold text-green-600 mb-1">Positive Drivers</h4>
                      {meta[selectedScore].positive_drivers.map((d, i) => <p key={i} className="text-xs text-[#64748B] flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-500" />{d}</p>)}
                    </div>
                  )}
                  {meta[selectedScore].negative_drivers?.length > 0 && (
                    <div><h4 className="text-xs font-semibold text-red-600 mb-1">Negative Drivers</h4>
                      {meta[selectedScore].negative_drivers.map((d, i) => <p key={i} className="text-xs text-[#64748B] flex items-center gap-1"><AlertCircle className="h-3 w-3 text-red-500" />{d}</p>)}
                    </div>
                  )}
                  <Separator />
                  <div className="bg-gray-50 p-3 rounded space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-[#64748B]">Source</span><span className="font-mono text-[10px] text-[#502D3C]">{meta[selectedScore].source}</span></div>
                    <div className="flex justify-between"><span className="text-[#64748B]">Direction</span><span className="text-[#502D3C]">{meta[selectedScore].good_direction === "higher_is_better" ? "Higher = better" : "Lower = better"}</span></div>
                  </div>
                </div>
              </ScrollArea>
            )}
          </SheetContent>
        </Sheet>

        {/* Glossary Sheet */}
        <Sheet open={showGlossary} onOpenChange={setShowGlossary}>
          <SheetContent className="w-[400px]">
            <SheetHeader><SheetTitle className="text-[#502D3C]">Glossary</SheetTitle></SheetHeader>
            <input placeholder="Search terms..." value={glossarySearch} onChange={e => setGlossarySearch(e.target.value)} className="mt-4 mb-3 w-full text-xs border rounded px-3 py-2" />
            <ScrollArea className="h-[calc(100vh-160px)]">
              <div className="space-y-3 pr-3">
                {Object.entries(glossary).filter(([term]) => term.toLowerCase().includes(glossarySearch.toLowerCase())).map(([term, def]) => (
                  <div key={term} className="p-3 bg-gray-50 rounded">
                    <h4 className="text-xs font-semibold text-[#502D3C] capitalize">{term.replace(/_/g, ' ')}</h4>
                    <p className="text-[11px] text-[#64748B] mt-1 leading-relaxed">{def}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}

// ===== HELPER COMPONENTS =====

function MetricPill({ label, value, alert }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] text-[#94A3B8]">{label}:</span>
      <span className={cn("text-[11px] font-medium", alert ? "text-red-600" : "text-[#502D3C]")}>{value}</span>
    </div>
  );
}

function MetricCard({ title, value, subValue, source, span = 2, alert }) {
  return (
    <Card className={cn("border-gray-100", `col-span-${span}`)}>
      <CardContent className="p-2.5">
        <div className="flex items-center justify-between mb-0.5">
          <p className="text-[9px] font-medium text-[#64748B] uppercase tracking-wider">{title}</p>
          <SourceIcon tooltip={source} />
        </div>
        <p className={cn("text-base font-semibold", alert ? "text-red-600" : "text-[#502D3C]")}>{value}</p>
        {subValue && <p className="text-[9px] text-[#94A3B8]">{subValue}</p>}
      </CardContent>
    </Card>
  );
}

function MiniChart({ title, data, dataKey, formatter, color, source, compact }) {
  if (!data || data.length === 0) return null;
  return (
    <Card className="border-gray-100">
      <CardContent className={compact ? "p-2" : "p-3"}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-semibold text-[#502D3C]">{title}</p>
          <SourceIcon tooltip={source} />
        </div>
        <div className={compact ? "h-[80px]" : "h-[120px]"}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs><linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.2} /><stop offset="95%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
              <XAxis dataKey="date" tick={{ fontSize: 8 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 8 }} tickLine={false} axisLine={false} tickFormatter={formatter} width={45} />
              <RechartsTooltip formatter={(v) => [formatter(v), title]} contentStyle={{ background: '#502D3C', border: 'none', borderRadius: 6, color: 'white', fontSize: 10 }} />
              <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#grad-${color})`} strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function InfoRow({ label, value, alert }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
      <span className="text-[11px] text-[#64748B]">{label}</span>
      <span className={cn("text-[11px] font-medium", alert ? "text-red-600" : "text-[#502D3C]")}>{typeof value === 'object' ? value : String(value)}</span>
    </div>
  );
}

function TrendBadge({ trend, customLabels }) {
  const label = customLabels?.[trend] || trend;
  const color = trend === "improving" || trend === "growing" ? "text-green-600 bg-green-50 border-green-200" :
                trend === "declining" ? "text-red-600 bg-red-50 border-red-200" : "text-[#64748B] bg-gray-50 border-gray-200";
  return <Badge variant="outline" className={cn("text-[9px] h-4 capitalize", color)}>{label}</Badge>;
}

function HealthBadge({ value }) {
  const color = value === "Stable" || value === "Healthy" ? "text-green-600 bg-green-50 border-green-200" :
                value === "Variable" || value === "Tightening" ? "text-amber-600 bg-amber-50 border-amber-200" :
                "text-red-600 bg-red-50 border-red-200";
  return <Badge variant="outline" className={cn("text-[9px] h-4", color)}>{value}</Badge>;
}
